import { Constructable } from "@spawn/common";
import { consumer, inject } from "@spawn/core";
import { FreegameWinPartyState as BaseFreegameWinPartyState } from "@spawn/freegames";
import { GoldenBonusService } from "@spawn/golden-bonus";
import {
	ActiveWinPartyModuleEvent,
	CounterEndModule,
	CountingModule,
	ParallelModules,
	PartyModule,
	StepperModule,
	WinPartyEvents,
} from "@spawn/slot-machine";
import { AlternatingLineStepperModule } from "../modules/alternatingLineStepperModule";
import { PingerModule } from "../modules/pingerModule";
import { NrgScatterModule } from "../modules/scatterModule";
import { NrgTeaserBellModule } from "../modules/teaserBellModule";
import { BaseSymbolTypes, SymbolService } from "../services/symbolService";

/** A game subclass keeps the name "FreegameWinPartyState" and replaces the module factories. */
@consumer
export class FreegameWinPartyState extends BaseFreegameWinPartyState {
	@inject protected symbolService!: SymbolService;
	@inject protected goldenBonusService!: GoldenBonusService;

	public override onEnter(): void {
		this.addButtonSubscriptions();
		this.setButtonStates();

		this.startWinParty();

		this.addSubscriptions();
	}

	// FDM-98: fixes skipping behavior as we don't use retrigger
	protected override shouldSkipToRetrigger(): boolean {
		return false;
	}

	protected override startWinParty(): void {
		const {
			slotGameBase: { paylines, winFactor, totalBet },
		} = this.networkService.state;

		this.partyService.setPaylineWins(paylines, winFactor, totalBet);

		void this.partyService.startModules([
			...(this.usePingerModule(paylines.length) ? [this.createPingerModule()] : []),
			this.createTeaserBellModule(),
			new CountingModule(),
			new CounterEndModule(),
			// in freegames every scatter is an extra scatter, so it shows the +1 animation
			...(this.hasScatter() ? this.createScatterModules() : []),
			new StepperModule(),
		]);
	}

	// If there are coins, the PingerModule starts even if there are no paylines.
	protected usePingerModule(paylineCount: number): boolean {
		return this.hasCoins() || paylineCount !== 0;
	}

	protected createPingerModule(): PartyModule {
		return new PingerModule();
	}

	protected createTeaserBellModule(): PartyModule {
		return new NrgTeaserBellModule();
	}

	protected createScatterModules(): PartyModule[] {
		return [new NrgScatterModule(0)];
	}

	protected alternatingLineStepperModule(): Constructable<PartyModule> {
		return AlternatingLineStepperModule;
	}

	// the alternating stepper groups coins and scatters into their own steps, so the scatters can show the +1 animation
	protected override addSubscriptions(): void {
		super.addSubscriptions();
		this.subscriptions.add(
			this.eventDispatcher.event$<ActiveWinPartyModuleEvent>(WinPartyEvents.ActiveWinPartyModule).subscribe(({ payload }) => {
				if (payload.moduleName === StepperModule.name && this.hasScatter()) {
					this.partyService.addParallelModule(ParallelModules.LineStepper, this.alternatingLineStepperModule());
				}
			})
		);
	}

	private hasScatter(): boolean {
		return this.symbolService.getScatterSymbolsCoords().length > 0;
	}

	private hasCoins(): boolean {
		return (
			this.goldenBonusService
				.getBonusSymbols()
				.filter((symbol) => ![BaseSymbolTypes.Scatter, BaseSymbolTypes.ScatterFG].includes(symbol.symbol as BaseSymbolTypes))
				.length > 0
		);
	}
}
